/**
 * Reusable PCB-trace backdrop — extracts the SVG vocabulary that
 * WelcomeScreen and the login pane established as a recognisable brand
 * surface, so the watermark can show up faintly behind the chat scroll
 * region, the empty Projects grid, and any other working surface that
 * would otherwise be flat navy.
 *
 * Variants pick the mask + density that suits the slot:
 *   • `welcome` — centred radial mask (the WelcomeScreen treatment).
 *   • `chat`    — top/bottom fade so messages stay legible against it.
 *   • `subtle`  — flat low-opacity tile, no mask. For card backgrounds.
 *
 * The pattern itself stays constant across variants — three trace runs
 * with bond-pad nodes, the same vocabulary the brand mark uses — so the
 * surfaces feel like the same circuit board even as the framing shifts.
 */
import { Box, alpha, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

type BackdropVariant = "welcome" | "chat" | "subtle";

interface PCBBackdropProps {
  /** Which mask + density to apply. */
  variant?: BackdropVariant;
  /** Tile size in CSS pixels. Larger = sparser. */
  tile?: number;
  /** Opacity multiplier on the trace strokes (0..1). */
  intensity?: number;
  /** Override the trace colour. Defaults to `primary.main`. */
  traceColor?: string;
  /** Children render above the backdrop. */
  children?: ReactNode;
  /** Extra sx layered onto the wrapper. */
  sx?: SxProps<Theme>;
}

export function PCBBackdrop({
  variant = "subtle",
  tile = 80,
  intensity = 1,
  traceColor,
  children,
  sx,
}: PCBBackdropProps) {
  const theme = useTheme();
  const stroke = traceColor ?? theme.palette.primary.main;
  // Encode trace stroke / fill colour into the inline SVG. Opacity is
  // baked into the stroke-opacity on each path so callers can dial the
  // density via `intensity` without touching the markup.
  const strokeOpacity = (0.12 * intensity).toFixed(3);
  const padOpacity = (0.22 * intensity).toFixed(3);
  const encColor = encodeURIComponent(stroke);
  const tracePattern = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><g fill='none' stroke='${encColor}' stroke-width='0.6' stroke-opacity='${strokeOpacity}'><path d='M0 20 L25 20 L25 45 L60 45 L60 80'/><path d='M40 0 L40 12 L70 12 L70 35'/><circle cx='25' cy='20' r='1.6' fill='${encColor}' fill-opacity='${padOpacity}'/><circle cx='60' cy='45' r='1.6' fill='${encColor}' fill-opacity='${padOpacity}'/><circle cx='40' cy='12' r='1.6' fill='${encColor}' fill-opacity='${padOpacity}'/></g></svg>")`;

  // Variant-specific overlay mask. The pattern is the same; what
  // changes is how much of it the user actually sees and where.
  const maskBg = (() => {
    const bg = theme.palette.background.default;
    if (variant === "welcome") {
      return `radial-gradient(ellipse 60% 55% at 50% 45%, ${alpha(bg, 0.92)}, transparent 80%)`;
    }
    if (variant === "chat") {
      // Fade top + bottom so the trace pattern only breathes through
      // the middle band — keeps message rows readable against it.
      return `linear-gradient(180deg, ${alpha(bg, 0.96)} 0%, ${alpha(bg, 0.55)} 18%, ${alpha(bg, 0.55)} 82%, ${alpha(bg, 0.96)} 100%)`;
    }
    // subtle — no overlay, the pattern is already low-opacity.
    return null;
  })();

  return (
    <Box
      sx={[
        {
          position: "relative",
          backgroundImage: tracePattern,
          backgroundSize: `${tile}px ${tile}px`,
          // The mask sits in `&::before` so it stacks above the
          // background image but below interactive children.
          ...(maskBg && {
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: maskBg,
              pointerEvents: "none",
              zIndex: 0,
            },
          }),
          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}
